import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ChartNoAxesColumn, Download, MoreHorizontal, Pencil, Plus, Search } from "lucide-react";
import { api, downloadFile, qs } from "@/lib/api";
import { formatDateTime, formatInt, formatShortDate, formatWhen } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { KIND_LABEL } from "./EventsPage";
import { RegisterDialog } from "./RegisterDialog";
import { EventDialog, SessionDialog, type EventRecord } from "./EventDialogs";

interface Session {
  id: number;
  name: string | null;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  capacity: number | null;
  demio_session_id?: string | null;
  /** The Demio webinars this class registers people into (several for the Virtual Boot Camp). */
  demio_parts?: { id: number }[];
  status: string;
  registered: number;
  attended: number;
  waitlisted: number;
  guests: number;
}
interface EventDetail {
  event: EventRecord;
  sessions: Session[];
}
interface Guest {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  checked_in_at: string | null;
}
interface RosterRow {
  registration_id: number;
  contact_id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  session_id: number | null;
  session: string;
  checked_in_at: string | null;
  guests_count: number;
  guests: Guest[];
  demio_join_url: string | null;
  demio_error?: string | null;
  source: string;
  notes: string | null;
  registered_at: string;
  imported: boolean;
  location_raw: string | null;
}
interface Roster {
  rows: RosterRow[];
  people: number;
  guests: number;
  seats: number;
  checked_in: number;
  waitlisted: number;
}

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted" | "default"> = {
  registered: "default",
  attended: "success",
  waitlisted: "warning",
  no_show: "muted",
  cancelled: "muted",
};

/** One event: its dates with counts, and the roster with check-in, guests, status changes and a CSV export. */
export function EventDetailPage() {
  const { eventId } = useParams({ from: "/events/$eventId" });
  const id = Number(eventId);
  const ev = useQuery({ queryKey: ["event", id], queryFn: () => api<EventDetail>(`/api/v1/events/${id}`) });
  const [session, setSession] = useState<number | "">("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [text, setText] = useState("");
  const [registering, setRegistering] = useState(false);
  const [editing, setEditing] = useState<"event" | "new-date" | Session | null>(null);
  const roster = useQuery({
    queryKey: ["roster", id, session, showCancelled],
    queryFn: () => api<Roster>(`/api/v1/events/${id}/roster` + qs({ session_id: session || undefined, include_cancelled: showCancelled || undefined })),
    placeholderData: (p) => p,
  });
  const toast = useToast();
  const rows = useMemo(() => {
    const t = text.trim().toLowerCase();
    return (roster.data?.rows ?? []).filter((r) => !t || `${r.name} ${r.email} ${r.phone} ${r.company}`.toLowerCase().includes(t));
  }, [roster.data, text]);
  if (ev.isLoading) return <Skeleton className="h-96" />;
  if (!ev.data) return <p className="text-sm text-[var(--color-destructive)]">{ev.error?.message ?? "Event not found."}</p>;
  const e = ev.data.event;
  const r = roster.data;
  const exportCSV = async () => {
    try {
      const name = await downloadFile(`/api/v1/events/${id}/roster.csv${qs({ session_id: session || undefined })}`, undefined, `roster-${id}.csv`);
      toast(`Downloaded ${name}`);
    } catch (err) {
      toast((err as Error).message, "error");
    }
  };
  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/events" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Events
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{e.name}</h1>
        <BrandMark id={e.brand_id} />
        <span className="text-sm text-[var(--color-muted-foreground)]">
          {KIND_LABEL[e.kind] ?? e.kind}
          {e.location ? `, ${e.location}` : ""}
        </span>
        <Badge variant={e.status === "scheduled" ? "default" : e.status === "cancelled" ? "destructive" : "muted"}>{e.status}</Badge>
        <span className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => setRegistering(true)} disabled={e.status === "cancelled"}>
            Register someone
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing("event")}>
            <Pencil /> Edit event
          </Button>
          <Button size="sm" variant="outline" onClick={exportCSV}>
            <Download /> Roster CSV
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/events/sales" search={{ event: id }}>
              <ChartNoAxesColumn /> Sales after it
            </Link>
          </Button>
        </span>
      </div>
      {e.description && <p className="text-sm text-[var(--color-muted-foreground)]">{e.description}</p>}

      <div className="flex flex-wrap gap-2">
        {ev.data.sessions.map((s) => (
          <div
            key={s.id}
            className={cn(
              "group relative rounded-md border bg-[var(--color-card)] hover:bg-[var(--color-muted)]",
              session === s.id && "border-[var(--color-primary)] bg-[var(--color-accent)]",
              s.status === "cancelled" && "row-dim",
            )}
          >
            <button onClick={() => setSession(session === s.id ? "" : s.id)} aria-pressed={session === s.id} className="block px-3 py-2 pr-9 text-left text-sm" title="Show only this date's roster">
              <div className="font-medium">
                {s.name || formatWhen(s.starts_at)}
                {s.status !== "scheduled" && (
                  <Badge variant={s.status === "cancelled" ? "destructive" : "muted"} className="ml-1.5">
                    {s.status}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {s.name ? formatWhen(s.starts_at) : ""}
                {s.location ? ` ${s.location}` : ""}
              </div>
              <div className="num mt-1 text-xs">
                {formatInt(s.registered)} registered{s.guests ? ` +${s.guests} guest${s.guests === 1 ? "" : "s"}` : ""}
                {s.capacity ? ` of ${s.capacity} seats` : ""}
                {s.waitlisted ? `, ${s.waitlisted} waitlisted` : ""}
                {s.attended ? `, ${s.attended} attended` : ""}
              </div>
            </button>
            <button
              aria-label={`Edit ${s.name || formatWhen(s.starts_at)}`}
              title="Edit this date"
              onClick={() => setEditing(s)}
              className="absolute top-1.5 right-1.5 rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-background)] hover:text-[var(--color-foreground)]"
            >
              <Pencil className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          onClick={() => setEditing("new-date")}
          className="flex min-h-16 items-center gap-1 rounded-md border border-dashed px-3 py-2 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
        >
          <Plus className="size-4" /> Add a date
        </button>
      </div>

      {editing === "event" && <EventDialog event={e} onClose={() => setEditing(null)} />}
      {editing === "new-date" && <SessionDialog event={e} onClose={() => setEditing(null)} />}
      {editing && typeof editing === "object" && <SessionDialog event={e} session={editing} onClose={() => setEditing(null)} />}
      {registering && <RegisterDialog eventId={id} sessions={ev.data.sessions} defaultSession={session || undefined} onClose={() => setRegistering(false)} />}
      <Card className="overflow-x-auto">
        <CardHeader className="flex-wrap">
          <CardTitle>Roster{session ? " for this date" : ""}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
              <Input className="h-8 w-56 pl-7" value={text} onChange={(x) => setText(x.target.value)} placeholder="Find on the roster" aria-label="Find on the roster" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
              <Checkbox checked={showCancelled} onChange={(x) => setShowCancelled(x.target.checked)} /> Show cancelled
            </label>
          </div>
        </CardHeader>
        {r && (
          <CardContent className="flex flex-wrap gap-6 border-b text-sm">
            <Stat n={r.people} label={r.people === 1 ? "person" : "people"} />
            <Stat n={r.guests} label={r.guests === 1 ? "guest" : "guests"} />
            <Stat n={r.seats} label="seats taken" />
            <Stat n={r.checked_in} label="checked in" />
            <Stat n={r.waitlisted} label="waitlisted" />
          </CardContent>
        )}
        {roster.isLoading && <Skeleton className="m-3 h-40" />}
        {!roster.isLoading && rows.length === 0 && <Empty title="Nobody on this roster yet." />}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH className="w-24">Checked in</TH>
                <TH>Person</TH>
                <TH>Phone</TH>
                <TH>Date</TH>
                <TH>Status</TH>
                <TH>Guests</TH>
                <TH>Registered</TH>
                <TH />
              </tr>
            </THead>
            <tbody>
              {rows.map((row) => (
                <RosterLine key={row.registration_id} row={row} eventId={id} sessions={ev.data!.sessions} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function sessionLabel(row: RosterRow, sessions: Session[]): string {
  const s = sessions.find((x) => x.id === row.session_id);
  if (s) return s.name || formatShortDate(s.starts_at);
  return row.session || row.location_raw || "—";
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div>
      <span className="num text-lg font-semibold">{formatInt(n)}</span> <span className="text-xs text-[var(--color-muted-foreground)]">{label}</span>
    </div>
  );
}

function RosterLine({ row, eventId, sessions }: { row: RosterRow; eventId: number; sessions: Session[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["roster", eventId] });
    void qc.invalidateQueries({ queryKey: ["event", eventId] });
  };
  const checkIn = useMutation({
    mutationFn: (v: { registration_id?: number; guest_id?: number; undo?: boolean }) => api("/api/v1/events/check-in", { json: v }),
    onSuccess: refresh,
    onError: (e) => toast(e.message, "error"),
  });
  const update = useMutation({
    mutationFn: (v: { status?: string; session_id?: number }) => api(`/api/v1/event-registrations/${row.registration_id}`, { method: "PATCH", json: v }),
    onSuccess: (_r, v) => {
      refresh();
      toast(v.status ? `${row.name}: ${v.status.replace("_", " ")}` : `${row.name} moved`);
    },
    onError: (e) => toast(e.message, "error"),
  });
  // Get the Demio link again (events_demio_retry): for the person's current class, when it reaches Demio.
  const session = sessions.find((s) => s.id === row.session_id);
  const hasDemio = !!(session?.demio_session_id || (session?.demio_parts?.length ?? 0) > 0 || row.demio_error);
  const demio = useMutation({
    mutationFn: () => api(`/api/v1/event-registrations/${row.registration_id}/demio`, { method: "POST" }),
    onSuccess: () => {
      refresh();
      toast(`${row.name}: Demio link updated`);
    },
    onError: (e) => toast(e.message, "error"),
  });
  const cancelled = row.status === "cancelled";
  return (
    <TR className={cn(cancelled && "row-dim")}>
      <TD>
        <label className="flex items-center gap-1.5">
          <Checkbox
            aria-label={`Check in ${row.name}`}
            checked={!!row.checked_in_at}
            disabled={cancelled || row.status === "waitlisted" || checkIn.isPending}
            onChange={(x) => checkIn.mutate({ registration_id: row.registration_id, undo: !x.target.checked })}
          />
          {row.checked_in_at && <span className="text-xs text-[var(--color-muted-foreground)]">{formatDateTime(row.checked_in_at).replace(/^\w+ \d+, \d+, /, "")}</span>}
        </label>
      </TD>
      <TD className="max-w-[16rem]">
        <Link to="/contacts/$contactId" params={{ contactId: String(row.contact_id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
          {row.name || `#${row.contact_id}`}
        </Link>
        <div className="truncate text-xs text-[var(--color-muted-foreground)]">
          {row.email}
          {row.company ? `, ${row.company}` : ""}
        </div>
      </TD>
      <TD className="num whitespace-nowrap">{row.phone || "—"}</TD>
      <TD className="max-w-[12rem] whitespace-nowrap text-xs" title={row.session || undefined}>
        {sessionLabel(row, sessions)}
        {row.demio_error && <div className="text-[var(--color-destructive)]">Demio: {row.demio_error}</div>}
      </TD>
      <TD>
        <Badge variant={STATUS_VARIANT[row.status] ?? "muted"}>{row.status.replace("_", " ")}</Badge>
        {row.imported && <div className="text-xs text-[var(--color-muted-foreground)]">from Keap</div>}
      </TD>
      <TD className="text-xs">
        {row.guests.length === 0 && "—"}
        {row.guests.map((g) => (
          <label key={g.id} className="flex items-center gap-1.5">
            <Checkbox
              aria-label={`Check in guest ${[g.first_name, g.last_name].filter(Boolean).join(" ")}`}
              checked={!!g.checked_in_at}
              disabled={cancelled || checkIn.isPending}
              onChange={(x) => checkIn.mutate({ guest_id: g.id, undo: !x.target.checked })}
            />
            {[g.first_name, g.last_name].filter(Boolean).join(" ") || g.email || "guest"}
          </label>
        ))}
      </TD>
      <TD className="whitespace-nowrap text-xs">{formatDateTime(row.registered_at)}</TD>
      <TD>
        <DropdownMenu>
          <DropdownMenuTrigger className="rounded p-0.5 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]" aria-label={`Actions for ${row.name}`}>
            <MoreHorizontal className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {row.status === "waitlisted" && <DropdownMenuItem onSelect={() => update.mutate({ status: "registered" })}>Move off the waitlist</DropdownMenuItem>}
            {row.status === "registered" && <DropdownMenuItem onSelect={() => update.mutate({ status: "no_show" })}>Mark as no-show</DropdownMenuItem>}
            {row.status === "registered" && hasDemio && <DropdownMenuItem onSelect={() => demio.mutate()}>Get the Demio link again</DropdownMenuItem>}
            {(row.status === "no_show" || row.status === "cancelled") && <DropdownMenuItem onSelect={() => update.mutate({ status: "registered" })}>Register again</DropdownMenuItem>}
            {sessions
              .filter((s) => s.id !== row.session_id && s.status === "scheduled")
              .map((s) => (
                <DropdownMenuItem key={s.id} onSelect={() => update.mutate({ session_id: s.id })}>
                  Move to {s.name || formatWhen(s.starts_at)}
                </DropdownMenuItem>
              ))}
            {!cancelled && (
              <DropdownMenuItem
                className="text-[var(--color-destructive)]"
                onSelect={() => window.confirm(`Cancel ${row.name}'s registration? Their seat opens up (the waitlist may move up). Refunds are separate.`) && update.mutate({ status: "cancelled" })}
              >
                Cancel registration…
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TD>
    </TR>
  );
}

